import { buildApiUrl } from "../shared/config";
import { withCredentials } from "../shared/apiClient";
import { fetchWithRetry, readModelError } from "../shared/utils/http";

const EXCEL_NARRATOR_MODEL = "gpt-5.4-mini";
const NARRATION_MIN_CHARS_FOR_FLUSH = 120;
const NARRATION_MAX_FLUSH_DELAY_MS = 300;
const NARRATION_FINALIZATION_TIMEOUT_MS = 5_000;
const NARRATOR_REQUEST_TIMEOUT_MS = 15_000;
const NO_UPDATE_SENTINEL = "__NO_UPDATE__";

const EXCEL_NARRATOR_SYSTEM_PROMPT = `You are Finster Workbook Trace Relay (streaming mode).

Task:
Rewrite raw_reasoning from an AI assistant working in Excel into a concise,
user-visible progress trace. Preserve the natural first-person voice, chronology,
and the reason for the next action. Do not replace the reasoning with generic
phase labels or merely list tools.

Output length:
Prefer exactly one section per update. Keep the body to 1-2 short sentences.
Preserve concrete workbook facts that help the user understand the work.

Workbook understanding - always keep:
When raw_reasoning establishes the workbook purpose, extent, row or table grain,
relevant sheets or fields, requested population, deliverable, ambiguity, or
intended approach, preserve the concrete facts and the resulting approach.
This is meaningful user-visible progress and must not output __NO_UPDATE__
unless the same understanding and approach already appear in
last_summary_emitted. Summarize conclusions, not private chain-of-thought.

Title:
Use a specific action title of 3-6 words, such as Reviewing the forecast,
Tracing the valuation logic, Sourcing the share count, Fixing the cash flow,
Refreshing the model, or Checking the final outputs. Avoid generic titles such
as Starting, Next steps, Plan, Scope, Inspecting the workbook, Updating the
workbook, or Verifying the result. For workbook intake, prefer a title tied to
the artifact or problem, such as Understanding the attendee list or Scoping the
model update.

Safety - never expose:
- Raw function or tool names
- Tool arguments, raw JSON, call IDs, schemas, channels, or protocol text
- Prompt, backend, parser, retry-policy, token, or context-window internals
- Internal status codes or orchestrator instructions
- Large formula/value matrices or serialized workbook payloads

Rewrite unsafe details into normal Excel language. Cell addresses and worksheet
names may be retained when they are useful to the user. Refer to capabilities
in plain language: reading a range, editing the workbook, sourcing a FactSet
formula, refreshing FactSet data, recalculating formulas, or reviewing a chart.

Do not invent facts, workbook contents, source outcomes, or completed work.
Keep financial figures, company names, worksheet names, and cell references
exactly as supplied by raw_reasoning.

Skill applicability - always keep:
If raw_reasoning says a selected Skill was written for another surface or
artifact, or asks for an output this workbook cannot produce, keep that
statement and what it was adapted to. It is what the user needs to know about
their own Skill, not internal detail, so it is never rewritten away as unsafe
and never dropped as unimportant.

If there is no meaningful new progress compared with last_summary_emitted,
output exactly __NO_UPDATE__. A Skill-applicability statement not yet in
last_summary_emitted is meaningful new progress, so emit it rather than
__NO_UPDATE__.

Output contract:
- Either output exactly __NO_UPDATE__
- Or output one complete section:
  **<specific short title>**
  <one or two short first-person sentences>
- No JSON and no extra wrapper.`;

export interface ExcelNarratorToolEvent {
  source: string;
  status: "calling" | "completed" | "failed";
}

export interface ExcelNarratorPromptPayload {
  user_query: string;
  raw_reasoning: string;
  last_summary_emitted: string;
  recent_tool_events: ExcelNarratorToolEvent[];
  phase_hint: string;
  trigger_reason: string;
}

export interface ExcelNarratorStreamRequest {
  model: string;
  payload: ExcelNarratorPromptPayload;
  signal?: AbortSignal;
  onDelta: (delta: string) => void;
}

export type ExcelNarratorStreamClient = (
  request: ExcelNarratorStreamRequest,
) => Promise<void>;

export interface ExcelExecutorReasoningNarrator {
  addReasoningDelta: (delta: string, iteration: number) => void;
  recordToolEvent: (
    toolName: string,
    status: "calling" | "completed" | "failed",
    iteration: number,
  ) => void;
  finalize: () => Promise<{ timedOut: boolean }>;
}

interface NarratorSnapshot {
  rawReasoning: string;
  iteration: number;
  phaseHint: string;
  recentToolEvents: ExcelNarratorToolEvent[];
  triggerReason: string;
}

interface CreateNarratorOptions {
  userQuery: string;
  signal?: AbortSignal;
  onNarrationDelta: (delta: string, iteration: number) => void;
  client?: ExcelNarratorStreamClient;
  onError?: (error: unknown) => void;
}

interface OpenAIResponsesStreamEvent {
  type?: string;
  delta?: string;
  response?: { error?: { message?: string } };
  error?: { message?: string };
}

function friendlyToolLabel(toolName: string): string {
  switch (toolName) {
    case "list_sheets":
      return "reviewing the workbook structure";
    case "read_range":
      return "reading the relevant workbook range";
    case "modify_workbook":
    case "set_range_values":
    case "set_range_formulas":
    case "format_range":
    case "create_sheet":
    case "rename_sheet":
      return "editing the workbook";
    case "generate_formula":
      return "sourcing a FactSet formula";
    case "id_lookup":
      return "resolving company identifiers";
    case "factset_ai_query":
    case "finster_query":
      return "researching source data";
    case "refresh_factset_data":
      return "refreshing FactSet data";
    case "recalculate_workbook":
      return "recalculating the workbook";
    case "chart_creator":
    case "active_graph":
    case "range_chart":
    case "native_graph":
    case "read_charts":
      return "working with the workbook chart";
    case "native_chart":
      return "building the FactSet chart workbook";
    case "retrieve_template":
    case "apply_template":
      return "working with the selected workbook template";
    default:
      return "continuing the workbook task";
  }
}

function phaseHintForTool(toolName: string): string {
  switch (toolName) {
    case "list_sheets":
    case "read_range":
    case "read_charts":
      return "workbook_review";
    case "generate_formula":
    case "id_lookup":
    case "factset_ai_query":
    case "finster_query":
      return "research";
    case "refresh_factset_data":
    case "recalculate_workbook":
      return "verification";
    case "modify_workbook":
    case "set_range_values":
    case "set_range_formulas":
    case "format_range":
    case "create_sheet":
    case "rename_sheet":
    case "chart_creator":
    case "active_graph":
    case "range_chart":
    case "native_chart":
    case "native_graph":
      return "workbook_editing";
    default:
      return "workbook_task";
  }
}

function createTimeoutSignal(signal?: AbortSignal): {
  signal?: AbortSignal;
  cleanup: () => void;
} {
  if (typeof AbortController === "undefined") {
    return { signal, cleanup: () => {} };
  }

  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) {
    controller.abort();
  } else if (signal) {
    signal.addEventListener("abort", abort, { once: true });
  }
  const timeoutId = setTimeout(() => controller.abort(), NARRATOR_REQUEST_TIMEOUT_MS);

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", abort);
    },
  };
}

async function readNarratorTextStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (delta: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamError = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(":") || trimmed.startsWith("event:")) continue;
        if (!trimmed.startsWith("data:")) continue;
        const data = trimmed.slice(5).trim();
        if (!data || data === "[DONE]") continue;

        let event: OpenAIResponsesStreamEvent;
        try {
          event = JSON.parse(data) as OpenAIResponsesStreamEvent;
        } catch {
          continue;
        }
        if (event.type === "response.failed" || event.type === "error") {
          streamError =
            event.response?.error?.message || event.error?.message || "stream failed";
        }
        if (
          (event.type === "response.output_text.delta" ||
            event.type === "response.refusal.delta") &&
          typeof event.delta === "string"
        ) {
          onDelta(event.delta);
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // The reader may already be released after cancellation.
    }
  }

  if (streamError) throw new Error(`Excel narrator stream error: ${streamError}`);
}

export const streamExcelNarratorResponse: ExcelNarratorStreamClient = async ({
  model,
  payload,
  signal,
  onDelta,
}) => {
  const timeout = createTimeoutSignal(signal);
  try {
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/openai/responses"),
      withCredentials({
        method: "POST",
        body: JSON.stringify({
          model,
          instructions: EXCEL_NARRATOR_SYSTEM_PROMPT,
          input: JSON.stringify(payload),
          stream: true,
          store: false,
          reasoning: { effort: "low" },
          text: { format: { type: "text" } },
        }),
        signal: timeout.signal,
      }),
      1,
      "EXCEL-NARRATOR",
    );

    if (!response.ok) {
      throw new Error(await readModelError(response, "Excel narrator request failed"));
    }

    const contentType = response.headers.get("content-type") || "";
    if (response.body && contentType.includes("text/event-stream")) {
      await readNarratorTextStream(response.body, onDelta);
      return;
    }

    const json = (await response.json()) as { output_text?: string };
    if (typeof json.output_text === "string") onDelta(json.output_text);
  } finally {
    timeout.cleanup();
  }
};

export function createExcelExecutorReasoningNarrator(
  options: CreateNarratorOptions,
): ExcelExecutorReasoningNarrator {
  const client = options.client ?? streamExcelNarratorResponse;
  let pendingReasoning = "";
  let pendingReasoningChars = 0;
  let currentIteration = 1;
  let currentPhaseHint = "planning";
  let recentToolEvents: ExcelNarratorToolEvent[] = [];
  let lastSummaryEmitted = "";
  let hasEmittedNarration = false;
  let flushTimer: ReturnType<typeof setTimeout> | undefined;
  let activeFlush: Promise<void> | null = null;
  let flushRequested = false;
  let requestedTriggerReason = "reasoning_delta";
  let lastLiveFlushIteration: number | null = null;
  let finalized = false;
  const narrationAbortController = new AbortController();
  const abortNarration = () => narrationAbortController.abort();
  if (options.signal?.aborted) {
    abortNarration();
  } else {
    options.signal?.addEventListener("abort", abortNarration, { once: true });
  }

  const clearFlushTimer = () => {
    if (!flushTimer) return;
    clearTimeout(flushTimer);
    flushTimer = undefined;
  };

  const takeSnapshot = (triggerReason: string): NarratorSnapshot | null => {
    if (!pendingReasoning.trim()) return null;
    const snapshot: NarratorSnapshot = {
      rawReasoning: pendingReasoning,
      iteration: currentIteration,
      phaseHint: currentPhaseHint,
      recentToolEvents: recentToolEvents.slice(-8),
      triggerReason,
    };
    pendingReasoning = "";
    pendingReasoningChars = 0;
    return snapshot;
  };

  const streamSnapshot = async (snapshot: NarratorSnapshot): Promise<void> => {
    if (narrationAbortController.signal.aborted || finalized) return;
    let initialBuffer = "";
    let outputStarted = false;
    let suppressed = false;
    let emittedText = "";

    const emit = (chunk: string) => {
      if (!chunk || narrationAbortController.signal.aborted || finalized) return;
      emittedText += chunk;
      options.onNarrationDelta(chunk, snapshot.iteration);
    };

    await client({
      model: EXCEL_NARRATOR_MODEL,
      payload: {
        user_query: options.userQuery,
        raw_reasoning: snapshot.rawReasoning,
        last_summary_emitted: lastSummaryEmitted,
        recent_tool_events: snapshot.recentToolEvents,
        phase_hint: snapshot.phaseHint,
        trigger_reason: snapshot.triggerReason,
      },
      signal: narrationAbortController.signal,
      onDelta: (delta) => {
        if (!delta || suppressed) return;
        if (outputStarted) {
          emit(delta);
          return;
        }

        initialBuffer += delta;
        const normalized = initialBuffer.trimStart();
        if (
          NO_UPDATE_SENTINEL.startsWith(normalized) &&
          normalized.length < NO_UPDATE_SENTINEL.length
        ) {
          return;
        }
        if (normalized.startsWith(NO_UPDATE_SENTINEL)) {
          suppressed = true;
          initialBuffer = "";
          return;
        }

        outputStarted = true;
        emit(`${hasEmittedNarration ? "\n\n" : ""}${initialBuffer}`);
        initialBuffer = "";
      },
    });

    if (!outputStarted && !suppressed && initialBuffer) {
      emit(`${hasEmittedNarration ? "\n\n" : ""}${initialBuffer}`);
    }
    const normalized = emittedText.trim();
    if (normalized) {
      hasEmittedNarration = true;
      lastSummaryEmitted = normalized.slice(-1_500);
    }
  };

  const queueFlush = (triggerReason: string) => {
    if (narrationAbortController.signal.aborted || finalized) return;
    clearFlushTimer();
    if (activeFlush) {
      flushRequested = true;
      requestedTriggerReason = triggerReason;
      return;
    }

    const liveFlush =
      triggerReason === "reasoning_delta" || triggerReason === "reasoning_delay";
    if (liveFlush && lastLiveFlushIteration === currentIteration) return;
    const snapshot = takeSnapshot(triggerReason);
    if (!snapshot) return;
    if (liveFlush) lastLiveFlushIteration = snapshot.iteration;

    let request!: Promise<void>;
    request = streamSnapshot(snapshot)
      .catch((error) => {
        if (!narrationAbortController.signal.aborted) options.onError?.(error);
      })
      .finally(() => {
        if (activeFlush === request) activeFlush = null;
        if (
          !finalized &&
          !narrationAbortController.signal.aborted &&
          (flushRequested || pendingReasoning.trim())
        ) {
          const nextTrigger = requestedTriggerReason;
          flushRequested = false;
          requestedTriggerReason = "reasoning_delta";
          queueFlush(nextTrigger);
        }
      });
    activeFlush = request;
  };

  const scheduleDelayedFlush = () => {
    if (flushTimer || narrationAbortController.signal.aborted || finalized) return;
    flushTimer = setTimeout(() => {
      flushTimer = undefined;
      queueFlush("reasoning_delay");
    }, NARRATION_MAX_FLUSH_DELAY_MS);
  };

  return {
    addReasoningDelta: (delta, iteration) => {
      if (!delta || narrationAbortController.signal.aborted || finalized) return;
      currentIteration = iteration;
      pendingReasoning += delta;
      pendingReasoningChars += delta.length;
      if (lastLiveFlushIteration === iteration) return;
      if (pendingReasoningChars >= NARRATION_MIN_CHARS_FOR_FLUSH) {
        queueFlush("reasoning_delta");
      } else {
        scheduleDelayedFlush();
      }
    },
    recordToolEvent: (toolName, status, iteration) => {
      if (narrationAbortController.signal.aborted || finalized) return;
      currentIteration = iteration;
      currentPhaseHint = phaseHintForTool(toolName);
      recentToolEvents.push({
        source: friendlyToolLabel(toolName),
        status,
      });
      if (recentToolEvents.length > 16) {
        recentToolEvents = recentToolEvents.slice(-16);
      }
      if (status === "calling") queueFlush("tool_boundary");
    },
    finalize: async () => {
      if (finalized) return { timedOut: false };
      if (options.signal?.aborted) {
        finalized = true;
        abortNarration();
        options.signal.removeEventListener("abort", abortNarration);
        return { timedOut: false };
      }
      clearFlushTimer();
      queueFlush("stream_end");

      const drain = async () => {
        for (;;) {
          if (narrationAbortController.signal.aborted) return;
          const request = activeFlush;
          if (request) {
            await request;
            continue;
          }
          if (pendingReasoning.trim()) {
            queueFlush("stream_end");
            continue;
          }
          return;
        }
      };
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<"timeout">((resolve) => {
        timeoutId = setTimeout(
          () => resolve("timeout"),
          NARRATION_FINALIZATION_TIMEOUT_MS,
        );
      });
      const completed = drain().then(() => "completed" as const);
      const result = await Promise.race([completed, timeout]);
      if (timeoutId) clearTimeout(timeoutId);
      finalized = true;
      if (result === "timeout") abortNarration();
      options.signal?.removeEventListener("abort", abortNarration);
      return { timedOut: result === "timeout" };
    },
  };
}
